import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaSearch, FaUserInjured, FaSort, FaFilter, FaCalendarAlt } from 'react-icons/fa';
import { useAuth } from './context/AuthContext';
import DoctorNavbar from './DocNav';
import { appointmentService } from './AppointmentsService';
import PatientDetailsModal from './PatientDetailsModal';


const PatientsList = () => {
  const { currentUser } = useAuth();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name'); // 'name', 'lastVisit', 'status'
  const [filterBy, setFilterBy] = useState('all'); // 'all', 'active', 'new', 'regular'
  const [showPatientModal, setShowPatientModal] = useState(false);
const [selectedPatientId, setSelectedPatientId] = useState(null);

  useEffect(() => {
    const fetchPatients = async () => {
      setLoading(true);
      try {
        const appointments = await appointmentService.getAppointmentsByDoctor(currentUser._id);
        
        // Extract unique patients from appointments
        const patientMap = new Map();
        
        appointments.forEach(apt => {
          const patientId = apt.patientId || 'unknown';
          const patientName = apt.patientName || 'Unknown Patient';
          const patientData = patientMap.get(patientId) || {
            id: patientId,
            name: patientName,
            visits: 0,
            lastVisit: null,
            upcomingAppointment: null,
            completedAppointments: [],
            status: 'active'
          };
          
          // Count this as a visit if completed
          if (apt.status === 'Completed') {
            patientData.visits += 1;
            patientData.completedAppointments.push(apt);
            
            // Update last visit if this is more recent
            const visitDate = new Date(apt.date);
            if (!patientData.lastVisit || visitDate > new Date(patientData.lastVisit)) {
              patientData.lastVisit = apt.date;
            }
          }
          
          // Check if this is an upcoming appointment
          const appointmentDate = new Date(apt.date);
          const today = new Date();
          if (appointmentDate >= today && apt.status !== 'Cancelled') {
            if (!patientData.upcomingAppointment || 
                appointmentDate < new Date(patientData.upcomingAppointment.date)) {
              patientData.upcomingAppointment = apt;
            }
          }
          
          // Determine status
          if (patientData.visits === 0) {
            patientData.status = 'new';
          } else if (patientData.visits > 3) {
            patientData.status = 'regular';
          }
          
          patientMap.set(patientId, patientData);
        });
        
        setPatients(Array.from(patientMap.values()));
      } catch (error) {
        console.error("Error fetching patients:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPatients();
  }, [currentUser]);
  
  // Filter and sort patients
  const filteredPatients = patients
    .filter(patient => {
      // Search filter
      const matchesSearch = patient.name.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Status filter
      const matchesFilter = 
        filterBy === 'all' || 
        patient.status === filterBy;
        
      return matchesSearch && matchesFilter;
    })
    .sort((a, b) => {
      // Sort patients
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'lastVisit':
          if (!a.lastVisit) return 1;
          if (!b.lastVisit) return -1;
          return new Date(b.lastVisit) - new Date(a.lastVisit);
        case 'status':
          return a.status.localeCompare(b.status);
        default:
          return 0;
      }
    });

  // Handle sort change
  const handleSortChange = (criteria) => {
    setSortBy(criteria);
  };

  return (
    <>
      <DoctorNavbar />
      <div className="container mx-auto px-4 py-32">
        <div className="bg-white p-6 rounded-lg shadow-lg">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
            <h1 className="text-3xl font-bold text-gray-800 mb-4 md:mb-0">
              Patient Management
            </h1>
            
            <div className="flex flex-col md:flex-row gap-3">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search patients..."
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full md:w-64"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              </div>
              
              <div className="flex gap-2">
                <select
                  className="px-3 py-2 border border-gray-300 rounded-md text-gray-700"
                  value={filterBy}
                  onChange={(e) => setFilterBy(e.target.value)}
                >
                  <option value="all">All Patients</option>
                  <option value="new">New Patients</option>
                  <option value="regular">Regular Patients</option>
                  <option value="active">Active Patients</option>
                </select>
                
                <Link 
                  to="/patients/new" 
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Add New Patient
                </Link>
              </div>
            </div>
          </div>
          
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                        onClick={() => handleSortChange('name')}
                      >
                        <div className="flex items-center">
                          Patient Name
                          {sortBy === 'name' && <FaSort className="ml-1" />}
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                        onClick={() => handleSortChange('lastVisit')}
                      >
                        <div className="flex items-center">
                          Last Visit
                          {sortBy === 'lastVisit' && <FaSort className="ml-1" />}
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Next Appointment
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                        onClick={() => handleSortChange('status')}
                      >
                        <div className="flex items-center">
                          Status
                          {sortBy === 'status' && <FaSort className="ml-1" />}
                        </div>
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredPatients.map((patient) => (
                      <tr key={patient.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="bg-blue-100 h-10 w-10 rounded-full flex items-center justify-center mr-3">
                              <span className="text-blue-600 font-medium">
                                {patient.name.split(' ').map(n => n[0]).join('')}
                              </span>
                            </div>
                            <div>
                              <div className="font-medium text-gray-900">{patient.name}</div>
                              <div className="text-sm text-gray-500">{patient.visits} visits</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {patient.lastVisit ? (
                            <div>
                              <div className="text-sm text-gray-900">
                                {new Date(patient.lastVisit).toLocaleDateString()}
                              </div>
                              <div className="text-xs text-gray-500">
                                {patient.completedAppointments.length > 0 && patient.completedAppointments[patient.completedAppointments.length - 1].reason}
                              </div>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-500">No previous visits</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {patient.upcomingAppointment ? (
                            <div>
                              <div className="flex items-center text-sm text-gray-900">
                                <FaCalendarAlt className="mr-1 text-blue-500" />
                                {new Date(patient.upcomingAppointment.date).toLocaleDateString()}
                              </div>
                              <div className="text-xs text-gray-500">
                                {new Date(patient.upcomingAppointment.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </div>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-500">No upcoming appointments</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full 
                            ${patient.status === 'new' ? 'bg-green-100 text-green-800' : 
                              patient.status === 'regular' ? 'bg-blue-100 text-blue-800' : 
                              'bg-gray-100 text-gray-800'}`}
                          >
                            {patient.status.charAt(0).toUpperCase() + patient.status.slice(1)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div 
  onClick={() => {
    console.log("View details for patient:", patient.id);
    setSelectedPatientId(patient.id); // Pass the actual patient ID
    setShowPatientModal(true);
  }} 
  className="text-blue-600 hover:text-blue-900 cursor-pointer"
>
  View Details
</div>
                          {/* <div to={`/appointments/book?patientId=${patient.id}`} className="text-green-600 hover:text-green-900">
                            Schedule
                          </div> */}
                        </td>
                      </tr>
                    ))}
                    {filteredPatients.length === 0 && (
                      <tr>
                        <td colSpan="5" className="px-6 py-12 text-center">
                          <FaUserInjured className="mx-auto text-gray-300 text-4xl mb-3" />
                          <p className="text-gray-500 text-lg">No patients found matching your criteria</p>
                          <p className="text-gray-400">Try adjusting your search or filter</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              
              <div className="mt-6 flex justify-between items-center">
                <div className="text-sm text-gray-500">
                  Showing {filteredPatients.length} of {patients.length} patients
                </div>
              </div>
            </>
            
          )}
     <PatientDetailsModal
  isOpen={showPatientModal}
  onClose={() => setShowPatientModal(false)}
  patientId={selectedPatientId} // Pass the selected ID directly
/>     
         
        </div>
      </div>
     
    </>
  );
};

export default PatientsList;